/**
 * Column sorting for the Tag mapping tables (Tag Mapping review 2026-09-28,
 * F09: clicking the five headings changed nothing and exposed no sort state).
 *
 * Kept out of the table components for the same reason `filters.ts` is: pure
 * functions of their arguments, testable without a DOM. The order itself is
 * applied by the SERVER — both lists are paginated, so sorting the loaded rows
 * would order 30 of 273 and lie about everything below them. The keys here are
 * the ones `tag_mapping_routes` accepts (`MATCHED_SORT_KEYS` / `PENDING_SORT_KEYS`).
 */

export type SortDirection = 'asc' | 'desc'

export interface TagMappingSort {
  key: string
  direction: SortDirection
}

/**
 * Every heading of both tables sorts (review B1, "clicking the five data
 * headings did not change row order"). The server orders the LINES the table
 * draws, over the whole filtered set, by the cell the heading names — on
 * Matched that means a tag's lines may separate, which is what a sorted flat
 * table looks like. On Pending, Mapped tag and Confidence make the server rank
 * a candidate for the whole queue first (cached), so the first such click can
 * take a few seconds longer.
 */
export const MATCHED_SORT_KEYS = [
  'pid_tag',
  'mapped_tag',
  'source_file',
  'source_type',
  'confidence',
] as const

export const PENDING_SORT_KEYS = MATCHED_SORT_KEYS

/**
 * The state after clicking a header — the Data Catalog's own toggle
 * (`CatalogTableV2.handleSort`): a first click on a column sorts it ascending,
 * clicking the sorted column again flips it, and clicking a DIFFERENT column
 * starts that one ascending. There is no "off" click: the Data Catalog has
 * none, and QA asked for the same control (review F09, "follow what we have
 * on data catalog"). The tab's default order is what shows until the first
 * click, and a reload brings it back.
 */
export const nextSort = (current: TagMappingSort | null, key: string): TagMappingSort => {
  if (!current || current.key !== key) return { key, direction: 'asc' }
  return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
}

/** The `aria-sort` a header carries: only the sorted column announces one. */
export const ariaSortFor = (
  sort: TagMappingSort | null,
  key: string,
): 'ascending' | 'descending' | undefined =>
  sort?.key === key ? (sort.direction === 'asc' ? 'ascending' : 'descending') : undefined
